import {escapePathSegments} from '../../utils.ts';
import type {DiffCollection} from '../types/file-diff-data.ts';
import type {PageDiffStatus} from '../types/pages-diff-status.ts';
import type {PcbComponentLibraryData, PcbData} from '../types/pcb-data.ts';
import type {ProjectData} from '../types/project-data.ts';
import type {SchematicComponentLibraryData, SchematicData} from '../types/schematic-data.ts';
import {type RequestOptions, makeApiCall} from './make-api-call.ts';

type BlobifiedSvg = Blob;

type GetProjectDataParams = {
  repoOwner: string;
  repoName: string;
  headCommit: string;
  baseCommit?: string;
  /** Including a documentPath will ensure you get the correct project info if there is more than one in the repo */
  documentPath?: string;
};
/**
 * Get project-level data for a given commit or commit range
 */
export async function getProjectData(params: GetProjectDataParams, options: RequestOptions = {}): Promise<ProjectData> {
  const range = params.baseCommit ? `${params.baseCommit}..${params.headCommit}` : params.headCommit;
  const documentPathSuffix = params.documentPath ? `/${escapePathSegments(params.documentPath)}` : '';
  const path = `${params.repoOwner}/${params.repoName}/project/compare/${range}${documentPathSuffix}`;

  const response = await makeApiCall({
    method: 'GET',
    path,
    headers: {
      'Prefer': 'respond-async',
    },
  }, {
    useCache: true,
    poll: true,
    ...options,
  });
  const projectData = await response.json() as ProjectData;
  return projectData;
}

export type DocumentData = PcbData | SchematicData | SchematicComponentLibraryData | PcbComponentLibraryData;
type GetDocumentParams = {
  repoOwner: string;
  repoName: string;
  documentPath: string;
  commit: string;
};
/**
 * Get the converted contents of a document at a particular commit
 */
export async function getDocumentData<D extends DocumentData = DocumentData>(params: GetDocumentParams, options: RequestOptions = {}): Promise<D> {
  const path = `${params.repoOwner}/${params.repoName}/preview/commit/${params.commit}/${escapePathSegments(params.documentPath)}`;
  const query = new URLSearchParams({
    type: 'json',
  });

  const response = await makeApiCall({
    method: 'GET',
    path,
    query,
    headers: {
      'Prefer': 'respond-async',
    },
  }, {
    useCache: true,
    poll: true,
    ...options,
  });
  const projectData = await response.json() as D;
  return projectData;
}

/**
 * Get the SVG representation of a document at a particular commit
 */
export async function getDocumentSvg(params: GetDocumentParams, options: RequestOptions = {}): Promise<BlobifiedSvg> {
  const path = `${params.repoOwner}/${params.repoName}/preview/commit/${params.commit}/${escapePathSegments(params.documentPath)}`;

  const response = await makeApiCall({
    method: 'GET',
    path,
    headers: {
      'Prefer': 'respond-async',
    },
  }, {
    useCache: true,
    poll: true,
    timeoutMs: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
  const projectData = await response.blob() as BlobifiedSvg;
  return projectData;
}

type GetPagesDiffStatusParams = {
  repoOwner: string;
  repoName: string;
  headCommit: string;
  baseCommit: string;
  headDocumentPath: string;
  baseDocumentPath?: string;
};
/**
 * For a multipage document, get the diff status for each page in the document from a given commit range
 */
export async function getPagesDiffStatus(params: GetPagesDiffStatusParams, options: RequestOptions = {}): Promise<PageDiffStatus[]> {
  const path = `${params.repoOwner}/${params.repoName}/diff_preview/diff_page/${params.headCommit}/${escapePathSegments(params.headDocumentPath)}`;
  const query = new URLSearchParams({
    base_commit: params.baseCommit,
    base_path: params.baseDocumentPath ?? params.headDocumentPath,
  });

  const response = await makeApiCall({
    method: 'GET',
    path,
    query,
    headers: {
      'Prefer': 'respond-async',
    },
  }, {
    useCache: true,
    poll: true,
    ...options,
  });
  const projectData = await response.json() as PageDiffStatus[];
  return projectData;
}

type GetDocumentDiffParams = {
  repoOwner: string;
  repoName: string;
  headCommit: string;
  baseCommit: string;
  headDocumentPath: string;
  baseDocumentPath?: string;
};
/**
 * Get the list of changes for document data for a given commit range
 */
export async function getDocumentDiffData(params: GetDocumentDiffParams, options: RequestOptions = {}): Promise<DiffCollection> {
  const path = `${params.repoOwner}/${params.repoName}/diff_preview/diff_list/${params.headCommit}/${escapePathSegments(params.headDocumentPath)}`;
  const query = new URLSearchParams({
    base_commit: params.baseCommit,
    base_path: params.baseDocumentPath ?? params.headDocumentPath,
  });

  const response = await makeApiCall({
    method: 'GET',
    path,
    query,
    headers: {
      'Prefer': 'respond-async',
    },
  }, {
    useCache: true,
    poll: true,
    ...options,
  });
  const projectData = await response.json() as DiffCollection;
  return projectData;
}

/**
 * Get an SVG representation of what was removed in a document for a given commit range
 */
export async function getDocumentDiffFromSvg(params: GetDocumentDiffParams, options: RequestOptions = {}): Promise<BlobifiedSvg> {
  const path = `${params.repoOwner}/${params.repoName}/diff_preview/from/${params.headCommit}/${escapePathSegments(params.headDocumentPath)}`;
  const query = new URLSearchParams({
    base_commit: params.baseCommit,
    base_path: params.baseDocumentPath ?? params.headDocumentPath,
  });

  const response = await makeApiCall({
    method: 'GET',
    path,
    query,
    headers: {
      'Prefer': 'respond-async',
    },
  }, {
    useCache: true,
    poll: true,
    timeoutMs: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
  const projectData = await response.blob() as BlobifiedSvg;
  return projectData;
}

/**
 * Get an SVG representation of what was added or changed in a document for a given commit range
 */
export async function getDocumentDiffToSvg(params: GetDocumentDiffParams, options: RequestOptions = {}): Promise<BlobifiedSvg> {
  const path = `${params.repoOwner}/${params.repoName}/diff_preview/to/${params.headCommit}/${escapePathSegments(params.headDocumentPath)}`;
  const query = new URLSearchParams({
    base_commit: params.baseCommit,
    base_path: params.baseDocumentPath ?? params.headDocumentPath,
  });

  const response = await makeApiCall({
    method: 'GET',
    path,
    query,
    headers: {
      'Prefer': 'respond-async',
    },
  }, {
    useCache: true,
    poll: true,
    timeoutMs: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
  const projectData = await response.blob() as BlobifiedSvg;
  return projectData;
}
